/** Componentes de estado: Spinner, Card, Badge, EmptyState, ErrorState. */
import type { ReactNode } from 'react';
import { Loader2, Inbox, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/cn';

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn('animate-spin', className)} aria-label="Cargando" />;
}

export function Cargando({ texto = 'Cargando...' }: { texto?: string }) {
  return (
    <div role="status" aria-live="polite" className="flex flex-col items-center justify-center gap-3 py-12 text-texto-suave">
      <Spinner className="h-8 w-8" />
      <span className="text-sm">{texto}</span>
    </div>
  );
}

export function Card({
  children,
  className,
  padding = true,
}: {
  children: ReactNode;
  className?: string;
  padding?: boolean;
}) {
  return (
    <div
      className={cn(
        'panel',
        padding && 'p-4',
        className,
      )}
    >
      {children}
    </div>
  );
}

const COLORES_BADGE = {
  gris: 'bg-superficie-alt text-texto dark:bg-superficie-alt dark:text-texto',
  verde: 'bg-exito-suave text-exito dark:bg-exito-suave/40 dark:text-exito',
  rojo: 'bg-peligro-suave text-peligro dark:bg-peligro-suave/40 dark:text-peligro',
  amarillo: 'bg-alerta-suave text-alerta dark:bg-alerta-suave/40 dark:text-alerta',
  azul: 'bg-info-suave text-info dark:bg-info-suave/40 dark:text-info',
} as const;

export function Badge({
  children,
  color = 'gris',
  className,
}: {
  children: ReactNode;
  color?: keyof typeof COLORES_BADGE;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center whitespace-nowrap rounded-sm px-2 py-0.5 text-xs font-medium',
        COLORES_BADGE[color],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function EmptyState({
  titulo = 'Sin datos',
  descripcion,
  icono,
  accion,
}: {
  titulo?: string;
  descripcion?: string;
  icono?: ReactNode;
  accion?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-4 py-12 text-center">
      <div className="text-texto-tenue dark:text-texto-tenue">{icono ?? <Inbox className="h-12 w-12" />}</div>
      <p className="font-medium text-texto dark:text-texto">{titulo}</p>
      {descripcion && <p className="max-w-sm text-sm text-texto-suave">{descripcion}</p>}
      {accion}
    </div>
  );
}

export function ErrorState({
  titulo = 'Ocurrio un error',
  descripcion,
  accion,
}: {
  titulo?: string;
  descripcion?: string;
  accion?: ReactNode;
}) {
  return (
    <div role="alert" className="flex flex-col items-center justify-center gap-3 px-4 py-8 text-center">
      <AlertTriangle className="h-6 w-6 text-peligro" />
      <p className="font-medium text-texto dark:text-texto">{titulo}</p>
      {descripcion && <p className="max-w-sm text-sm text-texto-suave">{descripcion}</p>}
      {accion}
    </div>
  );
}

/** Un fallo de red no se presenta como una lista vacía o un importe en cero. */
export function ErrorConsulta({ consulta }: { consulta: { refetch: () => unknown } }) {
  return <ErrorState titulo="No se pudo cargar la información" descripcion="Revisa la conexión e intenta nuevamente."
    accion={<button type="button" onClick={() => { void consulta.refetch(); }} className="min-h-9 rounded-md border border-borde-fuerte px-3 text-sm hover:bg-superficie-alt">Reintentar</button>} />;
}
